import { t } from '@sm/intl';
import { DetailsFeatureRules } from '~pricing/transformers/details/types';
import { Packages } from '~shared/constants/pricing';
import CELL_COPY from '~pricing/lib/PackageFeatures/details/cellCopy';
import experimentDetailsCopy from '~pricing/experiments/grow_2792_pnp_refresh_v1/copy/experimentDetailsCopy';
import { getTeamCollaborationValues } from '~pricing/experiments/grow_2792_pnp_refresh_v1/helpers';

const formsAndEnhanced = [
  Packages.AdvantageAnnual,
  Packages.AdvantageMonthly,
  Packages.PremierAnnual,
  Packages.TeamAdvantage,
  Packages.FormsAnnual,
  Packages.FormsMonthly,
];
const allExceptForms = [
  Packages.AdvantageAnnual,
  Packages.AdvantageMonthly,
  Packages.PremierAnnual,
  Packages.TeamAdvantage,
  Packages.TeamPremier,
  Packages.Enterprise,
];

const allExceptBasicFeatures = [
  'create_print_enabled',
  'download_as_ppt',
  'create_print_enabled',
  'text_formatting',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean => packageName !== Packages.Basic,
  }),
  {}
);

const allExceptFormsRule = [
  'grow_2792_recurring_surveys',
  'track_email_responses',
  'rating_question_type',
  'matrix_question_type',
  'collector_thank_you_enabled',
  'grow_2792_dashboard',
  'grow_2792_dashboard_pwd_protection',
  'extract_data',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean => allExceptForms.includes(packageName),
  }),
  {}
);

const teamCollaborationRule = [
  'add_users',
  'build_surveys_together',
  'analyze_results_together',
  'custom_templates',
  'team_themes',
  'shared_library',
  'collaboration',
  'team_ownership',
  'consolidated_billing',
  'user_management',
  'grow_2792_advanced_collaboration',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean | React.ReactElement => getTeamCollaborationValues(packageName),
  }),
  {}
);

const premierAndAboveRule = [
  'a_b_testing',
  'carry_forward',
  'response_quality_enabled',
  'grow_2792_word_cloud_combine_hide',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean =>
      [Packages.PremierAnnual, Packages.TeamPremier, Packages.Enterprise].includes(packageName),
  }),
  {}
);

const allPackagesRule = ['grow_2792_database_response_view'].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (): boolean => true,
  }),
  {}
);

export default {
  ...allPackagesRule,
  ...allExceptBasicFeatures,
  ...allExceptFormsRule,
  ...teamCollaborationRule,
  ...premierAndAboveRule,
  mobile_apps_ios_android: (): string | boolean => true,

  grow_2792_survey_create_limit: (): string => t(CELL_COPY.UNLIMITED),

  grow_2792_questions_per_form_survey: (packageName: Packages): string =>
    packageName === Packages.Basic ? t(CELL_COPY.create_question_limit, { amount: 10 }) : t(CELL_COPY.UNLIMITED),

  collector_create_limit: (packageName: Packages): string =>
    packageName === Packages.Basic
      ? t(experimentDetailsCopy.grow_2792_use_collectors, { number: 3 })
      : t(CELL_COPY.UNLIMITED),

  grow_2792_file_upload: (packageName: Packages): boolean => formsAndEnhanced.includes(packageName),

  analyze_combine_filters: (packageName: Packages): string | boolean => {
    return packageName === Packages.Basic ? t(CELL_COPY.BASIC_COMPARE_FILTER) : true;
  },

  analyze_trends_enabled: () => true,

  analyze_can_ta_tag: (packageName: Packages): string => {
    if (
      [
        Packages.AdvantageAnnual,
        Packages.AdvantageMonthly,
        Packages.TeamAdvantage,
        Packages.PremierAnnual,
        Packages.TeamPremier,
      ].includes(packageName)
    ) {
      return t(CELL_COPY.MANUAL);
    }

    if (packageName === Packages.Enterprise) {
      return experimentDetailsCopy.grow_2792_tag_written_answers_enterprise.defaultMessage;
    }

    return '';
  },

  grow_2792_customer_success_manager: (packageName: Packages): boolean => packageName === Packages.Enterprise,

  collect_contact_information: () => true,
} as DetailsFeatureRules;
